import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { Flag, MoreVert, Movie, Tv } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { generateClient } from 'aws-amplify/api';
import { isAdmin } from '../../utils/adminUtils';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = "https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api";

const client = generateClient();

const ContentReportsPage = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const navigate = useNavigate();
  const [movieReports, setMovieReports] = useState([]);
  const [showReports, setShowReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  const statusFilters = ['PENDING', 'REVIEWED', 'RESOLVED', 'DISMISSED'];

  useEffect(() => {
    if (isAdmin(user)) {
      fetchReports();
    }
  }, [user]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      
      const [movieResult, showResult] = await Promise.all([
        client.graphql({
          query: `
            query ListMovieReports {
              listMovieReports(limit: 100) {
                items {
                  id
                  movieID
                  reportedBy
                  reason
                  description
                  status
                  createdAt
                }
              }
            }
          `,
        }),
        client.graphql({
          query: `
            query ListShowReports {
              listShowReports(limit: 100) {
                items {
                  id
                  showID
                  reportedBy
                  reason
                  description
                  status
                  createdAt
                }
              }
            }
          `,
        })
      ]);
      
      const movieReports = movieResult.data.listMovieReports.items || [];
      const showReports = showResult.data.listShowReports.items || [];
      
      // Fetch related movie/show and user data
      const enrichedMovieReports = await Promise.all(
        movieReports.map(async (report) => {
          try {
            const [movieResult, userResult] = await Promise.all([
              // Fetch movie from MongoDB API
              fetch(`${API_BASE_URL}/movies/${report.movieID}`)
                .then(res => res.ok ? res.json() : null)
                .then(data => ({
                  data: {
                    getMovie: data ? {
                      id: data._id,
                      name: data.name,
                      poster: data.poster
                    } : null
                  }
                }))
                .catch(() => ({ data: { getMovie: null } })),
              // Fetch user from DynamoDB
              client.graphql({
                query: `query GetUser($id: ID!) { getUser(id: $id) { id displayName username } }`,
                variables: { id: report.reportedBy }
              }).then(result => {
                console.log('User fetch result for', report.reportedBy, ':', result);
                return result;
              }).catch(error => {
                console.warn('Failed to fetch user', report.reportedBy, ':', error);
                return { data: { getUser: null } };
              })
            ]);
            
            return {
              ...report,
              movie: movieResult.data.getMovie,
              reporter: userResult.data.getUser
            };
          } catch (error) {
            console.warn('Error fetching related data for movie report:', error);
            return report;
          }
        })
      );
      
      const enrichedShowReports = await Promise.all(
        showReports.map(async (report) => {
          try {
            const [showResult, userResult] = await Promise.all([
              // Fetch show from MongoDB API
              fetch(`${API_BASE_URL}/shows/${report.showID}`)
                .then(res => res.ok ? res.json() : null)
                .then(data => ({
                  data: {
                    getShow: data ? {
                      id: data._id,
                      name: data.name,
                      poster: data.poster
                    } : null
                  }
                }))
                .catch(() => ({ data: { getShow: null } })),
              // Fetch user from DynamoDB
              client.graphql({
                query: `query GetUser($id: ID!) { getUser(id: $id) { id displayName username } }`,
                variables: { id: report.reportedBy }
              }).then(result => {
                console.log('User fetch result for', report.reportedBy, ':', result);
                return result;
              }).catch(error => {
                console.warn('Failed to fetch user', report.reportedBy, ':', error);
                return { data: { getUser: null } };
              })
            ]);
            
            return {
              ...report,
              show: showResult.data.getShow,
              reporter: userResult.data.getUser
            };
          } catch (error) {
            console.warn('Error fetching related data for show report:', error);
            return report;
          }
        })
      );
      
      setMovieReports(enrichedMovieReports);
      setShowReports(enrichedShowReports);
    } catch (error) {
      console.error('Error fetching content reports:', error);
      addNotification({
        type: 'error',
        message: 'Failed to load content reports',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateReportStatus = async (reportId, status, type) => {
    try {
      const mutation = type === 'movie' ? 'updateMovieReport' : 'updateShowReport';
      const input = type === 'movie' ? 'UpdateMovieReportInput' : 'UpdateShowReportInput';
      
      const reports = type === 'movie' ? movieReports : showReports;
      const report = reports.find(r => r.id === reportId);
      
      await client.graphql({
        query: `
          mutation UpdateReport($input: ${input}!) {
            ${mutation}(input: $input) {
              id
              status
            }
          }
        `,
        variables: {
          input: { id: reportId, status }
        }
      });
      
      // Send notification to reporter about status change
      if (report?.reportedBy) {
        await client.graphql({
          query: `
            mutation CreateNotification($input: CreateNotificationInput!) {
              createNotification(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: {
              userID: report.reportedBy,
              type: status === 'RESOLVED' ? 'SUCCESS' : 'INFO',
              message: `Your report for ${type} has been ${status.toLowerCase()}.`,
              isRead: false,
              metadata: JSON.stringify({ reportId, type: `${type}_report_status` })
            }
          }
        });
      }
      
      if (type === 'movie') {
        setMovieReports(prev => prev.map(report => 
          report.id === reportId ? { ...report, status } : report
        ));
      } else {
        setShowReports(prev => prev.map(report => 
          report.id === reportId ? { ...report, status } : report
        ));
      }
      
      addNotification({
        type: 'success',
        message: `Report marked as ${status.toLowerCase()}`,
      });
    } catch (error) {
      console.error('Error updating report:', error);
      addNotification({
        type: 'error',
        message: 'Failed to update report status',
      });
    }
  };

  const handleMenuClick = (event, report, type) => {
    setAnchorEl(event.currentTarget);
    setSelectedReport({ ...report, type });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReport(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'REVIEWED': return 'info';
      case 'RESOLVED': return 'success';
      case 'DISMISSED': return 'default';
      default: return 'default';
    }
  };

  const allReports = [
    ...movieReports.map(r => ({ ...r, type: 'movie' })),
    ...showReports.map(r => ({ ...r, type: 'show' }))
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filteredReports = allReports.filter(report => {
    if (activeTab === 0) return true;
    return report.status === statusFilters[activeTab - 1];
  });

  if (!isAdmin(user)) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Content Reports
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage reports for movies and shows
      </Typography>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label={`All (${allReports.length})`} />
        {statusFilters.map((status, index) => (
          <Tab 
            key={status} 
            label={`${status} (${allReports.filter(r => r.status === status).length})`} 
          />
        ))}
      </Tabs>

      {filteredReports.length === 0 ? (
        <Alert severity="info">No content reports found.</Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredReports.map((report) => (
            <Grid item xs={12} md={6} key={`${report.type}-${report.id}`}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip 
                        icon={<Flag />}
                        label={report.status}
                        color={getStatusColor(report.status)}
                        size="small"
                      />
                      <Chip
                        icon={report.type === 'movie' ? <Movie /> : <Tv />}
                        label={report.type === 'movie' ? 'Movie' : 'Show'}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                    <IconButton 
                      size="small"
                      onClick={(e) => handleMenuClick(e, report, report.type)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  <Typography variant="h6" gutterBottom>
                    {report.reason}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    Reported by: {report.reporter?.displayName || report.reporter?.username || 'Unknown User'}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    {(report.movie?.poster || report.show?.poster) && (
                      <img 
                        src={report.movie?.poster || report.show?.poster} 
                        alt={report.movie?.name || report.show?.name}
                        style={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                        onClick={() => {
                          if (report.type === 'movie') {
                            navigate(`/movies/${report.movieID}`);
                          } else {
                            navigate(`/shows/${report.showID}`);
                          }
                        }}
                      />
                    )}
                    <Box>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ 
                          cursor: 'pointer', 
                          color: 'primary.main',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => {
                          if (report.type === 'movie') {
                            navigate(`/movies/${report.movieID}`);
                          } else {
                            navigate(`/shows/${report.showID}`);
                          }
                        }}
                      >
                        {report.movie?.name || report.show?.name || `${report.type === 'movie' ? 'Movie' : 'Show'} (ID: ${report.movieID || report.showID})`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {report.type === 'movie' ? 'Movie' : 'Show'} ID: {report.movieID || report.showID}
                      </Typography>
                    </Box>
                  </Box>

                  {report.description && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Details: {report.description}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(report.createdAt))} ago
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem key="reviewed" onClick={() => {
          updateReportStatus(selectedReport.id, 'REVIEWED', selectedReport.type);
          handleMenuClose();
        }}>
          Mark as Reviewed
        </MenuItem>
        <MenuItem key="resolved" onClick={() => {
          updateReportStatus(selectedReport.id, 'RESOLVED', selectedReport.type);
          handleMenuClose();
        }}>
          Mark as Resolved
        </MenuItem>
        <MenuItem key="dismissed" onClick={() => {
          updateReportStatus(selectedReport.id, 'DISMISSED', selectedReport.type);
          handleMenuClose();
        }}>
          Dismiss Report
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default ContentReportsPage;